// useCESDK.js
import { useState, useEffect, useCallback, useRef } from 'react';
import CreativeEditorSDK from '@cesdk/cesdk-js';
import supabase from '../utils/supabaseUtils';
import { v4 as uuidv4 } from 'uuid';

export function useCESDK() {
  const cesdkRef = useRef(null);
  const initializationAttemptedRef = useRef(false);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState(null);
  const [assetSources, setAssetSources] = useState({
    backgrounds: false,
    athletes: false,
    templates: false
  });

  // Initialize the editor
  const initialize = useCallback(async (container, options = {}) => {
    if (!container) {
      throw new Error('Container element is required for initialization');
    }
    
    // Prevent multiple initialization attempts
    if (initializationAttemptedRef.current) {
      console.warn("CESDK initialization already attempted, skipping");
      
      // If we already have an initialized instance, return it
      if (cesdkRef.current) {
        console.log("Returning existing CESDK instance");
        return cesdkRef.current;
      }
      
      // Reset the flag and try again if no instance exists (for recovery)
      console.log("No existing instance found, resetting initialization flag for recovery");
      initializationAttemptedRef.current = false;
    }
    
    initializationAttemptedRef.current = true;
    
    try {
      setStatus('initializing');
      
      // Configure CESDK with settings from the known working version
      const config = {
        license: import.meta.env.VITE_CESDK_LICENSE,
        baseURL: "/cesdk-assets",
        theme: "dark", // Use the built-in dark theme
        role: "Creator",
        i18n: {
          en: {
            // Simple labels - no complex category translations needed
            'libraries.baseball-backgrounds-entry.label': 'Baseball',
            'libraries.basketball-backgrounds-entry.label': 'Basketball',
            'libraries.football-backgrounds-entry.label': 'Football',
            'libraries.volleyball-backgrounds-entry.label': 'Volleyball',
            'libraries.softball-backgrounds-entry.label': 'Softball',
            'libraries.baseball-flags-backgrounds-entry.label': 'Baseball Flags',
            'libraries.cheerleader-backgrounds-entry.label': 'Cheerleader',
            'libraries.color-dust-backgrounds-entry.label': 'Color Dust',
            'libraries.color-lights-backgrounds-entry.label': 'Color Lights',
            'libraries.fire-smoke-backgrounds-entry.label': 'Fire & Smoke',
            'libraries.stadium-lights-backgrounds-entry.label': 'Stadium Lights'
          }
        },
        ui: { 
          elements: { 
            view: 'advanced', // 'advanced' was used in the working version
            panels: { settings: true },
            dock: { 
              enabled: true,
              placeholderOverlay: false
            },
            libraries: {
              enabled: true,
              // Only show libraries that we actually have content for
              entries: [
                // Our custom background sources will be added dynamically
                'baseball-backgrounds',
                'basketball-backgrounds', 
                'football-backgrounds',
                'volleyball-backgrounds',
                'color-lights-backgrounds',
                'stadium-lights-backgrounds',
                'softball-backgrounds',
                'baseball-flags-backgrounds',
                'cheerleader-backgrounds',
                'color-dust-backgrounds',
                'fire-smoke-backgrounds',
                // Default CESDK sources that we have assets for
                'ly.img.sticker',
                'ly.img.vectorpath', 
                'ly.img.typeface',
                'ly.img.filter.duotone',
                'ly.img.filter.lut',
                'ly.img.effect',
                'ly.img.blur'
                // Note: Not including image libraries since we don't have stock images
              ]
            },
            navigation: {
              enabled: true
            },
            pages: {
              enabled: true
            }
          }
        },
        callbacks: {
          onUpload: 'local', // Set to 'local' to enable the upload button immediately
          onRendered: () => {
            // Called when the editor has rendered - use this to update background color
            console.log("CESDK has rendered, setting background color");
            try {
              // Find all canvas elements
              const canvasEls = document.querySelectorAll('.ubq-editor-view__canvas');
              if (canvasEls.length > 0) {
                canvasEls.forEach(el => {
                  el.style.backgroundColor = '#4F6273';
                });
              }
            } catch (e) {
              console.warn("Error setting canvas background in onRendered:", e);
            }
          }
        },
        ...options
      };

      if (options.userId) {
        config.userId = options.userId;
      }

      if (options.callbacks) {
        // Merge callbacks, don't override completely
        config.callbacks = {
          ...config.callbacks,
          ...options.callbacks
        };
      }
      
      console.log("Initializing CESDK with config:", config);
      const instance = await CreativeEditorSDK.create(container, config);
      console.log("CESDK initialized successfully", instance);

      // Add default asset sources with absolute URL instead of relative URL
      instance.engine.addDefaultAssetSources({
        baseURL: `${window.location.origin}/cesdk-assets/imglyAssets`
      });

      // Apply custom background color for the editor
      if (instance && instance.ui) {
        try {
          // Inject CSS styles for the editor
          const injectStyles = () => {
            // Check if our style tag already exists
            let styleTag = document.getElementById('cesdk-custom-styles');
            if (!styleTag) {
              // Create a style tag if it doesn't exist
              styleTag = document.createElement('style');
              styleTag.id = 'cesdk-custom-styles';
              document.head.appendChild(styleTag);
            }
            
            // Define the CSS to inject
            const css = `
              /* Our custom class to identify the color */
              .playmaker-blue-bg {
                background-color: #4F6273 !important;
              }
              
              /* Target by role */
              [data-ubq-role="editor"],
              [data-ubq-role="canvas"],
              [data-ubq-role="canvas-container"] {
                background-color: #4F6273 !important;
              }
            
              :root {
                --cesdk-background-color: #4F6273 !important;
              }
              
              /* Direct color overrides for dark theme */
              .ubq-dark {
                --color-background: 79, 98, 115 !important;
              }
              
              /* Target all main editor containers with high specificity */
              body .ubq-layout.ubq-dark,
              body .ubq-editor-view__canvas-container.ubq-dark,
              body .ubq-dock-container.ubq-dark,
              body .ubq-editor-view__canvas-wrapper.ubq-dark,
              body .ubq-dock.ubq-dark {
                background-color: #4F6273 !important;
              }
              
              /* Target the specific dark theme containers */
              body .ubq-dark .ubq-editor-view__canvas-container,
              body .ubq-dark .ubq-dock-container,
              body .ubq-dark .ubq-editor-view__canvas-wrapper,
              body .ubq-dark .ubq-dock {
                background-color: #4F6273 !important;
              }
              
              /* Target the canvas background color */
              body .ubq-editor-view__canvas {
                background-color: #4F6273 !important;
              }
            `;
            
            // Set the CSS content
            styleTag.textContent = css;
            console.log("Injected custom background styles for editor");
          };
          
          // Inject styles immediately
          injectStyles();
          
          // Also try the direct property setting approach
          const setEditorBackground = () => {
            const color = '#4F6273';
            
            // Target all relevant editor elements with our background color
            const selectors = [
              '.ubq-layout',
              '.ubq-editor-view__canvas-container',
              '.ubq-dock-container',
              '.ubq-editor-view__canvas-wrapper',
              '.ubq-dock',
              '.ubq-editor-view__canvas'
            ];
            
            selectors.forEach(selector => {
              const elements = document.querySelectorAll(selector);
              elements.forEach(el => {
                el.style.backgroundColor = color;
                // Add our custom class for additional styling control
                el.classList.add('playmaker-blue-bg');
              });
            });
            
            // Also try setting CSS variables on the layout
            const layout = document.querySelector('.ubq-layout');
            if (layout) {
              layout.style.setProperty('--color-background', '79, 98, 115', 'important');
            }
            
            console.log("Applied custom background color to editor elements");
          };
          
          // Try immediately and after a delay to ensure DOM is loaded
          setEditorBackground();
          setTimeout(setEditorBackground, 500);
          setTimeout(setEditorBackground, 1000);
          
          // Also set up a MutationObserver to catch when the layout is added to DOM
          const observer = new MutationObserver((mutations) => {
            for (const mutation of mutations) {
              if (mutation.addedNodes.length) {
                if (document.querySelector('.ubq-layout')) {
                  setEditorBackground();
                  // Don't disconnect yet in case other elements are still loading
                  setTimeout(() => {
                    setEditorBackground();
                    observer.disconnect();
                  }, 1000);
                  break;
                }
              }
            }
          });
          
          // Start observing
          observer.observe(document.body, { childList: true, subtree: true });
          
          // Clean up after a reasonable time
          setTimeout(() => observer.disconnect(), 10000);
        } catch (e) {
          console.warn("Unable to customize editor background:", e);
        }
      }

      // Store in ref for stable cleanup
      cesdkRef.current = instance;
      
      // Make it available globally for debugging if needed
      window.cesdk = instance;
      
      // Additional debug info
      if (instance.engine && instance.ui) {
        console.log("Engine API available:", !!instance.engine);
        console.log("UI API available:", !!instance.ui);
        console.log("Asset API available:", !!(instance.engine && instance.engine.asset));
        
        // Try to log which blocks are visible in the dock
        try {
          if (instance.ui.dock) {
            console.log("Dock API available");
          } else {
            console.warn("Dock API not available in UI");
          }
        } catch (e) {
          console.warn("Error checking dock API:", e);
        }
      }
      
      setStatus('ready');
      return instance;
    } catch (err) {
      console.error('CESDK initialization error:', err);
      setError(err);
      setStatus('error');
      initializationAttemptedRef.current = false; // Reset on error to allow retry
      throw err;
    }
  }, []);

  // Clean up the editor
  const cleanup = useCallback(() => {
    if (cesdkRef.current) {
      try {
        // Properly dispose of the instance
        cesdkRef.current.dispose();
        // Clear the reference
        cesdkRef.current = null;
        // Clear global reference
        window.cesdk = null;
        // Reset state
        setStatus('idle');
        setAssetSources({
          backgrounds: false,
          athletes: false,
          templates: false
        });
        // Reset initialization flag
        initializationAttemptedRef.current = false;
      } catch (err) {
        console.error('Error during CESDK cleanup:', err);
      }
    }
  }, []);

  // Get all placeholders in a scene
  const getPlaceholders = useCallback(() => {
    const cesdk = cesdkRef.current;
    if (!cesdk) {
      console.error("CESDK not initialized in getPlaceholders");
      return [];
    }

    try {
      const scene = cesdk.engine.scene.get();
      if (!scene) {
        return [];
      }
      
            const getAllBlocksInScene = (scene) => {
        const result = [];
        
        try {
          const pages = cesdk.engine.block.getChildren(scene);
          
          pages.forEach((page) => {
            const pageChildren = cesdk.engine.block.getChildren(page);
            
            pageChildren.forEach(blockId => {
              if (cesdk.engine.block.isValid(blockId)) {
                result.push(blockId);
                
                // Add grandchildren (for nested blocks)
                try {
                  const grandchildren = cesdk.engine.block.getChildren(blockId);
                  grandchildren.forEach(grandchild => {
                    if (cesdk.engine.block.isValid(grandchild)) {
                      result.push(grandchild);
                    }
                  });
                } catch (e) {
                  // Ignore errors on getting children
                }
              }
            });
          });
          
          return result;
        } catch (blockTraversalError) {
          console.error("Error in block traversal:", blockTraversalError);
          return [];
        }
      };
      
      let blocks = [];
      try {
        blocks = getAllBlocksInScene(scene);
      } catch (traversalError) {
        console.error("Error in getAllBlocksInScene:", traversalError);
        return [];
      }
      
      const placeholders = [];
      
      for (const blockId of blocks) {
        try {
          if (!cesdk.engine.block.isValid(blockId)) continue;
          
          const name = cesdk.engine.block.getName(blockId) || "(unnamed)";
          const type = cesdk.engine.block.getType(blockId);
          
                    // Check various ways to identify placeholders
          let isPlaceholder = false;
          let detectionMethod = null;

          // Method 1: Check if block name is a known placeholder name
          const knownPlaceholderNames = [
            'Person', 'Athlete', 'Placeholder', 'Player', 'Name', 'First Name', 'LastName', 
            'Last Name', 'Team Name', 'Jersey Number', 'Position', 'School', 'Year', 
            'Coach', 'Stats', 'Number', 'Title', 'Subtitle', 'Header', 'Footer'
          ];
          if (knownPlaceholderNames.some(placeholderName => 
              name.toLowerCase().includes(placeholderName.toLowerCase()))) {
            isPlaceholder = true;
            detectionMethod = "name_pattern";
          }
          
          // Method 2: Check if block has placeholder behavior enabled (CESDK 1.52.0 API)
          try {
            if (cesdk.engine.block.supportsPlaceholderBehavior && 
                cesdk.engine.block.supportsPlaceholderBehavior(blockId)) {
              const behaviorEnabled = cesdk.engine.block.isPlaceholderBehaviorEnabled(blockId);
              if (behaviorEnabled) {
                isPlaceholder = true;
                detectionMethod = detectionMethod || "behavior_enabled";
              }
            }
          } catch (e) {
            // Ignore behavior check errors
          }
          
          // Method 3: Check if block has placeholder function enabled (CESDK 1.52.0 API)
          try {
            const placeholderEnabled = cesdk.engine.block.isPlaceholderEnabled && 
                cesdk.engine.block.isPlaceholderEnabled(blockId);
            if (placeholderEnabled) {
              isPlaceholder = true;
              detectionMethod = detectionMethod || "function_enabled";
            }
          } catch (e) {
            // Ignore function check errors
          }
          
          // Method 4: Check for legacy metadata approach
          try {
            const placeholderMeta = cesdk.engine.block.getMetadata(blockId, "isPlaceholder", "false");
            if (placeholderMeta === "true") {
              isPlaceholder = true;
              detectionMethod = detectionMethod || "metadata";
            }
          } catch (e) {
            // Ignore metadata check errors
          }
          
          if (isPlaceholder) {
            // Determine fill type and current content
            let fillType = "";
            let defaultContent = "";
            
            try {
              const fill = cesdk.engine.block.getFill(blockId);
              if (cesdk.engine.block.isValid(fill)) {
                fillType = cesdk.engine.block.getType(fill);
                
                // For image fills, try to get the current image URL
                if (fillType === "//ly.img.ubq/fill/image") {
                  try {
                    // Try different ways to get the image URL
                    try {
                      const sourceSet = cesdk.engine.block.getSourceSet(fill, "fill/image/sourceSet");
                      if (sourceSet && sourceSet.length > 0) {
                        defaultContent = sourceSet[0].uri;
                      }
                    } catch (e) {
                      try {
                        defaultContent = cesdk.engine.block.getString(fill, "fill/image/imageFileURI");
                      } catch (e2) {
                        // Can't get image URL
                      }
                    }
                  } catch (e) {
                    // Ignore error getting image URL
                  }
                }
              }
            } catch (e) {
              // Ignore errors getting fill
            }
            
            placeholders.push({
              id: blockId,
              name,
              type,
              fillType: fillType || "//ly.img.ubq/fill/color",
              defaultContent,
              detectionMethod
            });
          }
        } catch (err) {
          // Skip blocks with errors
          console.warn(`Error processing block for placeholder detection:`, err);
        }
      }
      
      console.log(`Found ${placeholders.length} placeholders in scene`);
      if (placeholders.length > 0) {
        console.log("Placeholder details:", placeholders.map(p => ({
          id: p.id,
          name: p.name,
          type: p.type,
          detectionMethod: p.detectionMethod
        })));
      }
      return placeholders;
    } catch (err) {
      console.error('Error getting placeholders:', err);
      return [];
    }
  }, []);

  // Set a block as placeholder using CESDK 1.52.0 API
  const setBlockAsPlaceholder = useCallback((blockId, isPlaceholder = true) => {
    const cesdk = cesdkRef.current;
    if (!cesdk) return false;
    
    try {
      if (isPlaceholder) {
        // Check if block supports placeholder behavior first
        if (cesdk.engine.block.supportsPlaceholderBehavior && 
            cesdk.engine.block.supportsPlaceholderBehavior(blockId)) {
          cesdk.engine.block.setPlaceholderBehaviorEnabled(blockId, true);
        }
        
        // Enable placeholder function
        cesdk.engine.block.setPlaceholderEnabled(blockId, true);
        
        // Also set metadata for backwards compatibility
        cesdk.engine.block.setMetadata(blockId, 'isPlaceholder', "true");
      } else {
        // Disable placeholder behavior
        if (cesdk.engine.block.supportsPlaceholderBehavior && 
            cesdk.engine.block.supportsPlaceholderBehavior(blockId)) {
          cesdk.engine.block.setPlaceholderBehaviorEnabled(blockId, false);
        }
        
        // Disable placeholder function
        cesdk.engine.block.setPlaceholderEnabled(blockId, false);
        
        // Remove metadata
        cesdk.engine.block.removeMetadata(blockId, 'isPlaceholder');
      }
      
      return true;
    } catch (error) {
      console.error('Error setting placeholder:', error);
      return false;
    }
  }, []);

  // Create a blank Instagram Story scene (1080x1920) - following CESDK 1.52.0 documentation
  const createBlankScene = useCallback(async (primaryImageUrl = null) => {
    const cesdk = cesdkRef.current;
    if (!cesdk) {
      throw new Error('CESDK not initialized');
    }

    try {
      console.log("Creating Instagram Story blank scene (1080x1920)...");
      
      // Try different scene creation approaches
      let scene = null;
      
      // Approach 1: Try createDesignScene if it exists
      if (typeof cesdk.createDesignScene === 'function') {
        console.log("📝 Trying createDesignScene()...");
        await cesdk.createDesignScene();
        scene = cesdk.engine.scene.get();
        console.log("📝 Scene after createDesignScene:", scene);
      }
      
      // Approach 2: If no scene yet, try addDefaultAssetSources + createDesignScene
      if (!scene && typeof cesdk.addDefaultAssetSources === 'function') {
        try {
          console.log("📝 Trying addDefaultAssetSources + createDesignScene...");
          cesdk.addDefaultAssetSources();
          await cesdk.createDesignScene();
          scene = cesdk.engine.scene.get();
          console.log("📝 Scene after addDefaultAssetSources:", scene);
        } catch (assetErr) {
          console.warn("⚠️ Asset source error (possibly already exists):", assetErr.message);
          // Try createDesignScene without adding asset sources
          if (typeof cesdk.createDesignScene === 'function') {
            await cesdk.createDesignScene();
            scene = cesdk.engine.scene.get();
            console.log("📝 Scene after retry createDesignScene:", scene);
          }
        }
      }
      
      // Approach 3: Manual creation if still no scene
      if (!scene) {
        console.log("📝 Trying manual scene creation...");
        scene = cesdk.engine.scene.create();
        console.log("📝 Created scene ID:", scene);
        
        // Create page
        const page = cesdk.engine.block.create('page');
        cesdk.engine.block.appendChild(scene, page);
        console.log("📝 Created page ID:", page);
      }
      
      if (!scene) {
        throw new Error('Failed to create scene - no scene created after all attempts');
      }
      
      // Get the page to set dimensions
      const pages = cesdk.engine.block.getChildren(scene);
      if (!pages || pages.length === 0) {
        throw new Error('No pages found in created scene');
      }
      
      const page = pages[0];
      
      // Set Instagram Story dimensions (1080x1920)
      cesdk.engine.block.setWidth(page, 1080);
      cesdk.engine.block.setHeight(page, 1920);
      
      console.log("✅ Instagram Story blank scene created successfully (1080x1920)");
      console.log("📄 Scene ID:", scene);
      console.log("📄 Page ID:", page);
      
      return scene;
    } catch (err) {
      console.error('Error creating Instagram Story blank scene:', err);
      throw err;
    }
  }, []);

  // Register asset sources
  const registerAssetSources = useCallback(async () => {
    const cesdk = cesdkRef.current;
    if (!cesdk || !cesdk.engine || !cesdk.engine.asset) {
      console.warn("CESDK asset API not available yet, skipping asset source registration");
      return false;
    }

    console.log("Registering asset sources...");
    
    try {
      // Add backgrounds source
      console.log("Registering backgrounds source");
      cesdk.engine.asset.addSource({
        id: 'supabase-backgrounds',
        findAssets: async () => {
          try {
            console.log("🔍 Starting background asset search...");
            const { data: folderData, error: folderError } = await supabase.storage
              .from('playmakerpics-images')
              .list('backgrounds', { limit: 100, offset: 0 });
            
            if (folderError) {
              console.error("❌ Error listing background folders:", folderError);
              throw folderError;
            }
            
            console.log("📂 Raw folder data:", folderData);
            
            let allAssets = [];
            // Only consider entries without extensions as folders (like 'BaseballBG', 'CheerleaderConfetti', etc.)
            const subfolders = folderData.filter(f => 
              !f.name.includes('.') && f.name !== '.emptyFolderPlaceholder'
            );
            
            console.log(`📁 Found ${subfolders.length} background category folders:`, subfolders.map(f => f.name));
            
            // Process each background category subfolder
            for (const folder of subfolders) {
              // Create a proper category name - format the folder name for better display
              const categoryName = folder.name
                .replace(/([A-Z])/g, ' $1') // Add space before capital letters (BaseballBG → Baseball BG)
                .replace(/^./, match => match.toUpperCase()); // Capitalize first letter
              
              console.log(`🏷️ Processing folder "${folder.name}" with category name "${categoryName}"`);
              
              const subPath = `backgrounds/${folder.name}`;
              const { data: fileData, error: fileError } = await supabase.storage
                .from('playmakerpics-images')
                .list(subPath, { limit: 100, offset: 0 });
              
              if (fileError) {
                console.error(`❌ Error listing files in ${subPath}:`, fileError);
                continue;
              }
              
              console.log(`📊 Found ${fileData.length} files in background category ${folder.name}:`, 
                fileData.map(f => f.name).join(', '));
              
              // Only include image files
              const subAssets = fileData
                .filter(file => /\.(jpg|jpeg|png|gif)$/i.test(file.name))
                .map(file => {
                  const encodedFileName = encodeURIComponent(file.name);
                  const fullPath = `${subPath}/${encodedFileName}`;
                  const { data: urlData } = supabase.storage
                    .from('playmakerpics-images')
                    .getPublicUrl(fullPath);
                    
                  if (!urlData || !urlData.publicUrl) {
                    console.warn(`⚠️ Could not get public URL for ${fullPath}`);
                    return null;
                  }
                    
                  const uri = `${urlData.publicUrl}?t=${Date.now()}`;
                  
                  return {
                    id: `bg-${folder.name}-${file.name.replace(/\//g, '-')}`,
                    label: file.name.split('.')[0] || "Background Image",
                    kind: 'image',
                    meta: {
                      uri,
                      thumbUri: uri,
                      blockType: '//ly.img.ubq/graphic',
                      fillType: '//ly.img.ubq/fill/image',
                      shapeType: '//ly.img.ubq/shape/rect',
                      width: 1920,
                      height: 1080,
                      mimeType: file.name.match(/\.(png|gif)$/i) ? "image/png" : "image/jpeg",
                      cacheBuster: Date.now(),
                      category: categoryName,  // Set the category to the formatted folder name
                    },
                  };
                })
                .filter(asset => asset !== null); // Remove null entries
              
              console.log(`🖼️ Created ${subAssets.length} assets for category "${categoryName}"`);
              allAssets = allAssets.concat(subAssets);
            }
            
            // Also check for images directly in the backgrounds folder (uncategorized)
            const rootImages = folderData.filter(f => /\.(jpg|jpeg|png|gif)$/i.test(f.name));
            if (rootImages.length > 0) {
              console.log(`📷 Found ${rootImages.length} uncategorized backgrounds:`, 
                rootImages.map(f => f.name).join(', '));
              
              const uncategorizedAssets = rootImages.map(file => {
                try {
                  const encodedFileName = encodeURIComponent(file.name);
                  const path = `backgrounds/${encodedFileName}`;
                  const { data: urlData } = supabase.storage
                    .from('playmakerpics-images')
                    .getPublicUrl(path);
                    
                  if (!urlData || !urlData.publicUrl) {
                    console.warn(`⚠️ Could not get public URL for ${path}`);
                    return null;
                  }
                    
                  const uri = `${urlData.publicUrl}?t=${Date.now()}`;
                  
                  return {
                    id: `bg-root-${file.name.replace(/\//g, '-')}`,
                    label: file.name.split('.')[0] || "Background Image",
                    kind: 'image',
                    meta: {
                      uri,
                      thumbUri: uri,
                      blockType: '//ly.img.ubq/graphic',
                      fillType: '//ly.img.ubq/fill/image',
                      shapeType: '//ly.img.ubq/shape/rect',
                      width: 1920,
                      height: 1080,
                      mimeType: file.name.match(/\.(png|gif)$/i) ? "image/png" : "image/jpeg",
                      cacheBuster: Date.now(),
                      category: "General",  // Use "General" for uncategorized backgrounds
                    },
                  };
                } catch (error) {
                  console.warn(`⚠️ Error processing file ${file.name}:`, error);
                  return null;
                }
              }).filter(asset => asset !== null);
              
              console.log(`🖼️ Created ${uncategorizedAssets.length} assets for "General" category`);
              allAssets = allAssets.concat(uncategorizedAssets);
            }
            
            // Add a test background if no assets found
            if (allAssets.length === 0) {
              console.log("⚠️ No background assets found, adding test asset");
              allAssets.push({
                id: 'test-background',
                label: 'Test Background',
                kind: 'image',
                meta: {
                  uri: 'https://picsum.photos/1920/1080',
                  thumbUri: 'https://picsum.photos/200/200',
                  blockType: '//ly.img.ubq/graphic',
                  fillType: '//ly.img.ubq/fill/image',
                  shapeType: '//ly.img.ubq/shape/rect',
                  width: 1920,
                  height: 1080,
                  mimeType: "image/jpeg",
                  category: "Sample"
                },
              });
            }
            
            // Log unique categories
            const categories = [...new Set(allAssets.map(asset => asset.meta.category))];
            console.log(`🏷️ Background asset categories (${categories.length}):`, categories);
            
            console.log(`✅ Successfully processed ${allAssets.length} background assets`);
            return { 
              assets: allAssets, 
              total: allAssets.length, 
              currentPage: 0, 
              nextPage: undefined 
            };
          } catch (error) {
            console.error("❌ Error fetching background images:", error);
            return {
              assets: [{
                id: 'test-background',
                label: 'Test Background',
                kind: 'image',
                meta: {
                  uri: 'https://picsum.photos/1920/1080',
                  thumbUri: 'https://picsum.photos/200/200',
                  blockType: '//ly.img.ubq/graphic',
                  fillType: '//ly.img.ubq/fill/image',
                  shapeType: '//ly.img.ubq/shape/rect',
                  width: 1920,
                  height: 1080,
                  mimeType: "image/jpeg",
                  category: "Sample"
                },
              }], 
              total: 1, 
              currentPage: 0, 
              nextPage: undefined
            };
          }
        }
      });
      
      // Add local stickers source from public/cesdk-assets/imglyAssets
      console.log("Registering stickers source");
      cesdk.engine.asset.addSource({
        id: 'local-imgly-stickers',
        findAssets: async () => {
          const stickerFiles = [
            'fire.png',
            'lightning.png',
            'goal.png'
            // Add more filenames here as needed
          ];

          const baseUri = `${window.location.origin}/cesdk-assets/imglyAssets/ly.img.sticker`;
          const assets = stickerFiles.map((file) => {
            const uri = `${baseUri}/${file}?t=${Date.now()}`;
            return {
              id: `sticker-${file}`,
              kind: 'sticker',  // Important: This ensures it appears in the stickers tab
              meta: {
                uri,
                thumbUri: uri,
                blockType: '//ly.img.ubq/graphic',
                fillType: '//ly.img.ubq/fill/image',
                shapeType: '//ly.img.ubq/shape/rect',
                width: 512,
                height: 512,
                mimeType: 'image/png',
                category: 'Custom Stickers',  // Optional: Groups them in a category
                cacheBuster: Date.now()
              }
            };
          });

          console.log(`Found ${assets.length} sticker assets at base URI: ${baseUri}`);
          return {
            assets,
            total: assets.length,
            currentPage: 0,
            nextPage: undefined
          };
        }
      });

      // Add athletes source
      console.log("Registering athletes source");
      cesdk.engine.asset.addSource({
        id: 'supabase-athlete-images',
        findAssets: async () => {
          try {
            // First check if we have categories in subfolders
            const { data: folders, error: folderError } = await supabase.storage
              .from('playmakerpics-images')
              .list('athletes', { limit: 100, offset: 0 });
            
            if (folderError) {
              console.error("Error listing athlete folders:", folderError);
              throw folderError;
            }
            
            const assets = [];
            const categories = new Set(); // Track unique categories
            
            // Check if we have subfolders (directories don't have extensions)
            const subfolders = folders.filter(item => 
              !item.name.includes('.') && item.name !== '.emptyFolderPlaceholder'
            );
            
            // If we have subfolders, use them as categories
            if (subfolders.length > 0) {
              console.log(`Found ${subfolders.length} category folders:`, subfolders.map(f => f.name));
              
              // Process each subfolder as a category
              for (const folder of subfolders) {
                const categoryName = folder.name.charAt(0).toUpperCase() + folder.name.slice(1); // Capitalize
                categories.add(categoryName);
                
                // List files in this category folder
                const { data: files, error: filesError } = await supabase.storage
                  .from('playmakerpics-images')
                  .list(`athletes/${folder.name}`, { limit: 100, offset: 0 });
                  
                if (filesError) {
                  console.warn(`Error listing files in category ${folder.name}:`, filesError);
                  continue;
                }
                
                console.log(`Found ${files.length} files in category ${folder.name}`);
                
                // Process each file in this category
                for (const file of files) {
                  if (!/\.(jpg|jpeg|png|gif)$/i.test(file.name)) continue;
                  
                  try {
                    const encodedFileName = encodeURIComponent(file.name);
                    const path = `athletes/${folder.name}/${encodedFileName}`;
                    const { data: urlData } = supabase.storage
                      .from('playmakerpics-images')
                      .getPublicUrl(path);
                    
                    if (!urlData || !urlData.publicUrl) {
                      console.warn(`Could not get public URL for ${path}`);
                      continue;
                    }
                    
                    // Add cache busting parameter
                    const uri = `${urlData.publicUrl}?t=${Date.now()}`;
                    
                    const asset = {
                      id: `athlete-${folder.name}-${file.name.replace(/\s+/g, '-')}`,
                      label: file.name.split('.')[0] || "Placeholder Image",
                      kind: 'image',
                      meta: {
                        uri,
                        thumbUri: uri,
                        blockType: '//ly.img.ubq/graphic',
                        fillType: '//ly.img.ubq/fill/image',
                        shapeType: '//ly.img.ubq/shape/rect',
                        width: 1080,
                        height: 720,
                        mimeType: /\.(png|gif)$/i.test(file.name) ? "image/png" : "image/jpeg",
                        cacheBuster: Date.now(),
                        category: categoryName, // Add category
                      },
                    };
                    
                    assets.push(asset);
                  } catch (assetErr) {
                    console.warn(`Error processing file ${file.name} in category ${folder.name}:`, assetErr);
                  }
                }
              }
            } else {
              // No subfolders, use root files with default categorization
              const files = folders.filter(item => 
                /\.(jpg|jpeg|png|gif)$/i.test(item.name) && item.name !== '.emptyFolderPlaceholder'
              );
              
              console.log(`Found ${files.length} files in root athletes folder`);
              
              // Try to extract categories from filenames (e.g., "football_player1.jpg" → "Football")
              for (const file of files) {
                try {
                  // Try to extract category from filename
                  let category = "General";
                  const nameParts = file.name.split('_');
                  if (nameParts.length > 1) {
                    category = nameParts[0].charAt(0).toUpperCase() + nameParts[0].slice(1);
                    categories.add(category);
                  } else {
                    // If no underscore, try to guess from common sports terms
                    const sportsTerms = ["football", "baseball", "soccer", "basketball", "tennis", 
                                        "hockey", "golf", "swimming", "volleyball", "track"];
                    for (const sport of sportsTerms) {
                      if (file.name.toLowerCase().includes(sport)) {
                        category = sport.charAt(0).toUpperCase() + sport.slice(1);
                        categories.add(category);
                        break;
                      }
                    }
                  }
                  
                  const encodedFileName = encodeURIComponent(file.name);
                  const path = `athletes/${encodedFileName}`;
                  const { data: urlData } = supabase.storage
                    .from('playmakerpics-images')
                    .getPublicUrl(path);
                  
                  if (!urlData || !urlData.publicUrl) {
                    console.warn(`Could not get public URL for ${path}`);
                    continue;
                  }
                  
                  // Add cache busting parameter
                  const uri = `${urlData.publicUrl}?t=${Date.now()}`;
                  
                  const asset = {
                    id: `athlete-${file.name.replace(/\s+/g, '-')}`,
                    label: file.name.split('.')[0] || "Placeholder Image",
                    kind: 'image',
                    meta: {
                      uri,
                      thumbUri: uri,
                      blockType: '//ly.img.ubq/graphic',
                      fillType: '//ly.img.ubq/fill/image',
                      shapeType: '//ly.img.ubq/shape/rect',
                      width: 1080,
                      height: 720,
                      mimeType: /\.(png|gif)$/i.test(file.name) ? "image/png" : "image/jpeg",
                      cacheBuster: Date.now(),
                      category: category, // Add category
                    },
                  };
                  
                  assets.push(asset);
                } catch (assetErr) {
                  console.warn(`Error processing file ${file.name}:`, assetErr);
                }
              }
            }
            
            // Add a test athlete if no assets found
            if (assets.length === 0) {
              console.log("No valid placeholder images found, adding test image");
              assets.push({
                id: 'test-athlete',
                label: 'Test Placeholder',
                kind: 'image',
                meta: {
                  uri: 'https://picsum.photos/1080/720',
                  thumbUri: 'https://picsum.photos/200/200',
                  blockType: '//ly.img.ubq/graphic',
                  fillType: '//ly.img.ubq/fill/image',
                  shapeType: '//ly.img.ubq/shape/rect',
                  width: 1080,
                  height: 720,
                  mimeType: "image/png",
                  category: "Sample"
                },
              });
              categories.add("Sample");
            }
            
            console.log(`Successfully processed ${assets.length} placeholder assets in ${categories.size} categories`);
            return { 
              assets, 
              total: assets.length, 
              currentPage: 0, 
              nextPage: undefined 
            };
          } catch (error) {
            console.error("Error fetching athlete images:", error);
            return {
              assets: [{
                id: 'test-athlete',
                label: 'Test Placeholder',
                kind: 'image',
                meta: {
                  uri: 'https://picsum.photos/1080/720',
                  thumbUri: 'https://picsum.photos/200/200',
                  blockType: '//ly.img.ubq/graphic',
                  fillType: '//ly.img.ubq/fill/image',
                  shapeType: '//ly.img.ubq/shape/rect',
                  width: 1080,
                  height: 720,
                  mimeType: "image/png",
                  category: "Sample"
                },
              }], 
              total: 1, 
              currentPage: 0, 
              nextPage: undefined
            };
          }
        }
      });
      
      setAssetSources(prev => ({
        ...prev,
        backgrounds: true,
        athletes: true,
        stickers: true
      }));
      
      console.log("All asset sources registered successfully");
      return true;
    } catch (error) {
      console.error("Error registering asset sources:", error);
      return false;
    }
  }, []);

  // Load templates from Supabase
  const loadTemplates = useCallback(async () => {
    const cesdk = cesdkRef.current;
    if (!cesdk || !cesdk.engine || !cesdk.engine.asset) {
      console.warn("CESDK asset API not available, skipping template loading");
      return [];
    }

    try {
      console.log("Loading templates...");
      
      // Fetch templates from Supabase
      const { data: templates, error } = await supabase
        .from('templates')
        .select('*')
        .order('updated_at', { ascending: false });
      
      if (error) {
        throw error;
      }
      
      if (!templates || templates.length === 0) {
        console.warn("No templates found");
        return [];
      }
      
      // Add templates to CESDK
      const templateSourceId = 'playmaker-templates';
      
      try {
        cesdk.engine.asset.removeSource(templateSourceId);
      } catch (e) {
        console.log("Source didn't exist yet, creating new");
      }
      
      try {
        cesdk.engine.asset.addLocalSource(templateSourceId);
        
        templates.forEach(template => {
          if (!template.scene_url) {
            console.warn(`Skipping template ${template.id} due to missing scene_url`);
            return;
          }
          
          cesdk.engine.asset.addAssetToSource(templateSourceId, {
            id: `template-${template.id}`,
            label: template.name || "Untitled Template",
            kind: "template",
            meta: {
              uri: template.scene_url,
              thumbUri: template.thumbnail_url || 'https://picsum.photos/400/300',
              category: template.category || "General",
              templateId: template.id // Store the actual template ID for loading
            }
          });
        });
        
        cesdk.engine.asset.assetSourceContentsChanged(templateSourceId);
        
        console.log("Templates loaded successfully");
      } catch (error) {
        console.error("Error adding templates to asset source:", error);
      }
      
      setAssetSources(prev => ({
        ...prev,
        templates: true
      }));
      
      return templates;
    } catch (err) {
      console.error('Error loading templates:', err);
      return [];
    }
  }, []);

  // In the configureUI function, ensure we're adding the "Image Uploads" section correctly
  const configureUI = useCallback(() => {
    const cesdk = cesdkRef.current;
    if (!cesdk || !cesdk.ui) {
      console.warn("CESDK UI not available, skipping UI configuration");
      return false;
    }

    try {
      console.log("Configuring UI...");
      
      // Configure template library
      try {
        cesdk.ui.addAssetLibraryEntry({
          id: "playmaker-templates-entry",
          sourceIds: ["playmaker-templates"],
          sceneMode: "Design",
          previewLength: 5,
          previewBackgroundType: "cover",
          gridBackgroundType: "cover",
          gridColumns: 3,
          callbacks: {
            onAssetSelect: async (asset) => {
              console.log("Template selected:", asset);
              if (asset.kind === "template" && asset.meta?.templateId) {
                if (window.handleTemplateSelection) {
                  await window.handleTemplateSelection(asset.meta.templateId);
                  return true; // Prevent default handling
                }
              }
              return false; // Allow default handling for non-templates
            }
          }
        });
      } catch (e) {
        console.warn("Error adding templates entry:", e);
      }

      // Configure image uploads entry
      try {
        cesdk.ui.addAssetLibraryEntry({
          id: "image-uploads-entry",
          label: "Upload Image",
          sourceIds: ["ly.img.upload"],
          sceneMode: "Design",
          canAdd: true
        });
        console.log("Added image uploads entry");
      } catch (e) {
        console.warn("Error adding image uploads entry:", e);
      }
      
      // Configure user uploads entry
      try {
        cesdk.ui.addAssetLibraryEntry({
          id: "user-uploads-entry",
          label: "My Uploads",
          sourceIds: ["user-uploads"],
          sceneMode: "Design",
          previewLength: 5,
          previewBackgroundType: "cover",
          gridBackgroundType: "cover",
          gridColumns: 3,
        });
        console.log("Added user uploads entry");
      } catch (e) {
        console.warn("Error adding user uploads entry:", e);
      }

      // Configure athletes/placeholders library - adding it to the Images section
      try {
        cesdk.ui.addAssetLibraryEntry({
          id: "athlete-images-entry",
          label: "Placeholders",
          sourceIds: ["supabase-athlete-images"],
          sceneMode: "Design",
          previewLength: 5,
          previewBackgroundType: "cover",
          gridBackgroundType: "cover",
          gridColumns: 3,
          gridMode: "category",
          categoryLabel: "Sport Type",
          addAsset: async (asset) => {
            console.log("🏃‍♂️ Adding athlete asset:", asset);
            try {
              const cesdk = cesdkRef.current;
              if (!cesdk || !cesdk.engine || !asset.meta) {
                console.error("CESDK not ready or asset invalid");
                return false;
              }

              // Create a graphic block and add the image as fill
              const block = cesdk.engine.block.create('graphic');
              
              // Set block properties
              cesdk.engine.block.setWidth(block, asset.meta.width || 400);
              cesdk.engine.block.setHeight(block, asset.meta.height || 300);
              
              // Create shape
              const shape = cesdk.engine.block.createShape('rect');
              cesdk.engine.block.setShape(block, shape);
              
              // Create image fill
              const fill = cesdk.engine.block.createFill('image');
              cesdk.engine.block.setSourceSet(fill, 'fill/image/sourceSet', [
                { uri: asset.meta.uri, width: asset.meta.width || 400, height: asset.meta.height || 300 }
              ]);
              cesdk.engine.block.setFill(block, fill);
              
              // Get the scene and current page
              const scene = cesdk.engine.scene.get();
              const pages = cesdk.engine.block.getChildren(scene);
              const currentPage = pages[0]; // Use first page
              
              if (currentPage) {
                // Add to current page
                cesdk.engine.block.appendChild(currentPage, block);
                
                // Center the block on the page
                const pageWidth = cesdk.engine.block.getWidth(currentPage);
                const pageHeight = cesdk.engine.block.getHeight(currentPage);
                const blockWidth = cesdk.engine.block.getWidth(block);
                const blockHeight = cesdk.engine.block.getHeight(block);
                
                cesdk.engine.block.setPositionX(block, (pageWidth - blockWidth) / 2);
                cesdk.engine.block.setPositionY(block, (pageHeight - blockHeight) / 2);
                
                console.log("✅ Athlete asset added successfully");
                return true;
              } else {
                console.error("No page found to add asset to");
                return false;
              }
            } catch (error) {
              console.error("Error adding athlete asset:", error);
              return false;
            }
          }
        });
        console.log("Added athlete images entry");
      } catch (e) {
        console.warn("Error adding athlete images entry:", e);
      }

      // Configure backgrounds library with categories
      try {
        // Remove legacy backgrounds entry if it exists
        try {
          cesdk.ui.removeAssetLibraryEntry?.("sports-backgrounds-entry");
        } catch (_) {/* ignore */}

        console.log("🔍 Adding simplified backgrounds library entry …");
        
        // Add individual background entries for each sport
        const priorityBackgroundEntries = [
          { id: 'baseball-backgrounds-entry', label: 'Baseball', sourceId: 'baseball-backgrounds', icon: '⚾' },
          { id: 'basketball-backgrounds-entry', label: 'Basketball', sourceId: 'basketball-backgrounds', icon: '🏀' },
          { id: 'football-backgrounds-entry', label: 'Football', sourceId: 'football-backgrounds', icon: '🏈' },
          { id: 'volleyball-backgrounds-entry', label: 'Volleyball', sourceId: 'volleyball-backgrounds', icon: '🏐' },
          { id: 'color-lights-backgrounds-entry', label: 'Color Lights', sourceId: 'color-lights-backgrounds', icon: '💡' },
          { id: 'stadium-lights-backgrounds-entry', label: 'Stadium Lights', sourceId: 'stadium-lights-backgrounds', icon: '🏟️' }
        ];
        
        // Additional entries to add later
        const additionalBackgroundEntries = [
          { id: 'softball-backgrounds-entry', label: 'Softball', sourceId: 'softball-backgrounds', icon: '🥎' },
          { id: 'baseball-flags-backgrounds-entry', label: 'Baseball Flags', sourceId: 'baseball-flags-backgrounds', icon: '🏁' },
          { id: 'cheerleader-backgrounds-entry', label: 'Cheerleader', sourceId: 'cheerleader-backgrounds', icon: '📣' },
          { id: 'color-dust-backgrounds-entry', label: 'Color Dust', sourceId: 'color-dust-backgrounds', icon: '🎨' },
          { id: 'fire-smoke-backgrounds-entry', label: 'Fire & Smoke', sourceId: 'fire-smoke-backgrounds', icon: '🔥' }
        ];

        // Add priority entries first
        priorityBackgroundEntries.forEach(({ id, label, sourceId, icon }) => {
          cesdk.ui.addAssetLibraryEntry({
            id,
            label,
            sourceIds: [sourceId],
            sceneMode: "Design",
            previewLength: 3, // Reduced for speed
            gridColumns: 4,
            icon: () => icon,
            previewBackgroundType: "cover",
            gridBackgroundType: "cover",
            gridItemHeight: "square",
            cardLabelPosition: () => "below",
            canAdd: true,
            addAsset: async (asset) => {
              console.log(`🎯 [Library Entry ${id}] addAsset called for:`, asset.id);
              // Trigger our custom preview modal instead of adding directly
              if (window.showBackgroundPreview) {
                console.log(`🎯 [Library Entry ${id}] Calling showBackgroundPreview`);
                window.showBackgroundPreview(sourceId, asset.id);
                return true; // Indicate we handled the operation
              } else {
                console.error(`🎯 [Library Entry ${id}] showBackgroundPreview not available!`);
                return false; // Fallback to default behavior
              }
            }
          });
        });
        
        // Add additional entries after delay
        setTimeout(() => {
          additionalBackgroundEntries.forEach(({ id, label, sourceId, icon }) => {
            cesdk.ui.addAssetLibraryEntry({
              id,
              label,
              sourceIds: [sourceId],
              sceneMode: "Design",
              previewLength: 3,
              gridColumns: 4,
              icon: () => icon,
              previewBackgroundType: "cover",
              gridBackgroundType: "cover",
              gridItemHeight: "square",
              cardLabelPosition: () => "below",
              canAdd: true,
              addAsset: async (asset) => {
                console.log(`🎯 [Additional Library Entry ${id}] addAsset called for:`, asset.id);
                // Trigger our custom preview modal instead of adding directly
                if (window.showBackgroundPreview) {
                  console.log(`🎯 [Additional Library Entry ${id}] Calling showBackgroundPreview`);
                  window.showBackgroundPreview(sourceId, asset.id);
                  return true; // Indicate we handled the operation
                } else {
                  console.error(`🎯 [Additional Library Entry ${id}] showBackgroundPreview not available!`);
                  return false; // Fallback to default behavior
                }
              }
            });
          });
          
          // Update dock order to include new entries
          const allEntries = [
            ...priorityBackgroundEntries.map(e => e.id),
            ...additionalBackgroundEntries.map(e => e.id)
          ];
          
          try {
            cesdk.ui.setDockOrder([
              { 
                id: "ly.img.assetLibrary.dock", 
                key: "templates", 
                icon: "@imgly/Template", 
                label: "Templates", 
                entries: ["playmaker-templates-entry"] 
              },
              { 
                id: "ly.img.assetLibrary.dock", 
                key: "supabase-backgrounds", 
                icon: "@imgly/Image", 
                label: "Backgrounds", 
                entries: allEntries
              },
              { 
                id: "ly.img.assetLibrary.dock", 
                key: "ly.img.image", 
                icon: "@imgly/Image", 
                label: "Images", 
                entries: ["image-uploads-entry", "user-uploads-entry", "athlete-images-entry", "ly.img.image"] 
              },
              { 
                id: "ly.img.assetLibrary.dock", 
                key: "ly.img.vectorpath", 
                icon: "@imgly/Shapes", 
                label: "Shapes", 
                entries: ["ly.img.vectorpath"] 
              },
              { 
                id: "ly.img.assetLibrary.dock", 
                key: "ly.img.sticker", 
                icon: "@imgly/Sticker", 
                label: "Stickers", 
                entries: ["ly.img.sticker"] 
              },
              { 
                id: "ly.img.assetLibrary.dock", 
                key: "ly.img.text", 
                icon: "@imgly/Text", 
                label: "Text", 
                entries: ["ly.img.text"] 
              }
            ], { editMode: "Transform" });
            console.log("✅ Updated dock with all background entries");
          } catch (e) {
            console.warn("Could not update dock order:", e);
          }
        }, 2500); // Slight delay after sources are loaded
        
        console.log(`✅ Added ${priorityBackgroundEntries.length} priority background entries (${additionalBackgroundEntries.length} more loading...)`);
        
      } catch (e) {
        console.error("❌ Error adding backgrounds entries:", e);
      }

      // ===== DOCK ORDER ===== (Using hardcoded IDs since variables may be out of scope)
      try {
        cesdk.ui.setDockOrder([
          { 
            id: "ly.img.assetLibrary.dock", 
            key: "templates", 
            icon: "@imgly/Template", 
            label: "Templates", 
            entries: ["playmaker-templates-entry"] 
          },
          { 
            id: "ly.img.assetLibrary.dock", 
            key: "supabase-backgrounds", 
            icon: "@imgly/Image", 
            label: "Backgrounds", 
            entries: [
              "baseball-backgrounds-entry", "basketball-backgrounds-entry", "football-backgrounds-entry",
              "volleyball-backgrounds-entry", "color-lights-backgrounds-entry", "stadium-lights-backgrounds-entry"
            ] // Initial priority entries - additional ones added via setTimeout
          },
          { 
            id: "ly.img.assetLibrary.dock", 
            key: "ly.img.image", 
            icon: "@imgly/Image", 
            label: "Images", 
            entries: ["image-uploads-entry", "user-uploads-entry", "athlete-images-entry", "ly.img.image"] 
          },
          { 
            id: "ly.img.assetLibrary.dock", 
            key: "ly.img.vectorpath", 
            icon: "@imgly/Shapes", 
            label: "Shapes", 
            entries: ["ly.img.vectorpath"] 
          },
          { 
            id: "ly.img.assetLibrary.dock", 
            key: "ly.img.sticker", 
            icon: "@imgly/Sticker", 
            label: "Stickers", 
            entries: ["ly.img.sticker"] 
          },
          { 
            id: "ly.img.assetLibrary.dock", 
            key: "ly.img.text", 
            icon: "@imgly/Text", 
            label: "Text", 
            entries: ["ly.img.text"] 
          }
        ], { editMode: "Transform" });
        
        console.log("Dock order set successfully");
      } catch (e) {
        console.error("Error setting dock order:", e);
      }

      console.log("UI configured successfully");
      return true;
    } catch (err) {
      console.error('Error configuring UI:', err);
      return false;
    }
  }, []);

  // Load a scene from Supabase by template ID
  const loadSceneFromSupabase = useCallback(async (templateId) => {
    if (!templateId) {
      throw new Error('Template ID is required');
    }
    
    console.log(`Starting to load template with ID: ${templateId}`);
    
    try {
      // Fetch the template details
      const { data: template, error } = await supabase
        .from('templates')
        .select('*')
        .eq('id', templateId)
        .single();
      
      if (error || !template) {
        throw new Error(error?.message || 'Template not found');
      }
      
      if (!template.scene_url) {
        throw new Error('Template has no scene URL');
      }
      
      console.log(`Template found: ${template.name}, loading scene from: ${template.scene_url}`);
      
      // Fetch the scene data
      const response = await fetch(template.scene_url, { cache: 'no-store' });
      if (!response.ok) {
        throw new Error(`Failed to fetch scene: ${response.statusText}`);
      }
      
      let sceneText = await response.text();
      if (!sceneText.startsWith('UBQ')) {
        throw new Error('Invalid scene data format');
      }
      
      console.log(`Scene data loaded successfully, length: ${sceneText.length}`);
      
      // Get the existing CESDK instance
      const cesdk = cesdkRef.current;
      
      // Load the scene data directly
      if (cesdk.engine && cesdk.engine.scene && cesdk.engine.scene.loadFromString) {
        await cesdk.engine.scene.loadFromString(sceneText);
        console.log("Scene loaded successfully using engine.scene.loadFromString");
      } else if (cesdk.loadFromString) {
        await cesdk.loadFromString(sceneText);
        console.log("Scene loaded successfully using cesdk.loadFromString");
      } else {
        throw new Error("No method available to load scene from string");
      }
      
      console.log("Template loaded successfully");
      
      return {
        id: templateId,
        name: template.name,
        styleCategory: template.style_category,
        styleSubcategory: template.style_subcategory
      };
    } catch (err) {
      console.error('Error loading scene from Supabase:', err);
      throw err;
    }
  }, []);

  // Save the current scene to Supabase
  const saveSceneToSupabase = useCallback(async (name, userId, styleCategory, styleSubcategory, templateId = null, isPublic = false) => {
    const cesdk = cesdkRef.current;
    if (!cesdk) {
      throw new Error('CESDK not initialized');
    }
    try {
      // Check if user is admin - with error handling
      let isAdmin = false;
      try {
        const { data: userData, error: userError } = await supabase
          .from('app_users')
          .select('is_admin')
          .eq('id', userId)
          .single();
          
        if (!userError && userData) {
          isAdmin = userData.is_admin || false;
        } else {
          console.warn("Could not verify admin status, defaulting to non-admin");
        }
      } catch (adminCheckError) {
        console.warn("Error checking admin status:", adminCheckError);
        // Continue with non-admin privileges
      }
      
      // If trying to make template public, verify admin status
      if (isPublic && !isAdmin) {
        throw new Error('Only administrators can create public templates');
      }
      
      // If updating existing template, check ownership and public status
      if (templateId) {
        const { data: existingTemplate, error: templateError } = await supabase
          .from('templates')
          .select('user_id, is_public')
          .eq('id', templateId)
          .single();
          
        if (templateError) throw templateError;
        
        // If user is not the owner and template is public, create a new private template
        if (existingTemplate.user_id !== userId && existingTemplate.is_public) {
          templateId = null; // Force creation of new template
          isPublic = false; // Force private for non-owners
        }
        // If user is not the owner and template is private, throw error
        else if (existingTemplate.user_id !== userId && !existingTemplate.is_public) {
          throw new Error('You do not have permission to edit this template');
        }
      }
      
      // -------------------------------------------
      // 1) Gather placeholder metadata
      // -------------------------------------------
      let placeholderData = [];
      try {
        placeholderData = getPlaceholders(); // util already returns array of objects
      } catch (e) {
        console.warn('Could not gather placeholder data:', e);
      }

      // -------------------------------------------
      // 2) Serialize the scene
      // -------------------------------------------
      const sceneData = await cesdk.engine.scene.saveToString();

      // -------------------------------------------
      // 3) Create a thumbnail image
      // -------------------------------------------
      let thumbnailUrl = null;
      try {
        const scene = cesdk.engine.scene.get();
        const thumbnailBlob = await cesdk.engine.block.export(scene, 'image/png', {
          targetWidth: 400,
          targetHeight: 300,
          dpi: 72,
          exportArea: 'design'
        });

        const thumbFileName = `${uuidv4()}-thumbnail.png`;
        const { error: thumbErr } = await supabase.storage
          .from('playmakerpics-images')
          .upload(`thumbnails/${thumbFileName}`, thumbnailBlob, {
            upsert: true,
            contentType: 'image/png'
          });

        if (!thumbErr) {
          thumbnailUrl = supabase.storage
            .from('playmakerpics-images')
            .getPublicUrl(`thumbnails/${thumbFileName}`).data.publicUrl;
        } else {
          console.warn('Thumbnail upload failed, using fallback', thumbErr);
        }
      } catch (thumbGenErr) {
        console.warn('Unable to generate thumbnail, using fallback', thumbGenErr);
      }

      // Upload scene to storage
      const timestamp = Date.now();
      const storagePath = `templates/${userId}/template-${timestamp}.ubq`;
      
      const { error: uploadError } = await supabase.storage
        .from('playmakerpics-images')
        .upload(storagePath, sceneData, {
          contentType: 'application/octet-stream',
          upsert: true
        });
        
      if (uploadError) throw uploadError;
      
      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('playmakerpics-images')
        .getPublicUrl(storagePath);
        
      // Save template record
      const templateData = {
        name,
        user_id: userId,
        scene_url: publicUrl,
        thumbnail_url: thumbnailUrl || 'https://picsum.photos/400/300',
        placeholder_data: placeholderData,
        style_category: styleCategory,
        style_subcategory: styleSubcategory,
        is_public: isPublic,
        updated_at: new Date().toISOString()
      };
      
      let response;
      if (templateId) {
        // Update existing template
        response = await supabase
          .from('templates')
          .update(templateData)
          .eq('id', templateId)
          .select();
      } else {
        // Create new template
        response = await supabase
          .from('templates')
          .insert([templateData])
          .select();
      }
      
      if (response.error) throw response.error;
      if (!response.data || response.data.length === 0) {
        throw new Error('No data returned from template update');
      }
      
      return response.data[0];
    } catch (error) {
      console.error('Error saving scene to Supabase:', error);
      throw error;
    }
  }, [getPlaceholders]);

  // Configure image upload
  const configureImageUpload = useCallback(async (userId) => {
    const cesdk = cesdkRef.current;
    if (!cesdk) {
      console.warn("CESDK not initialized, skipping image upload configuration");
      return false;
    }

    if (!userId) {
      console.warn("User ID is required for image upload configuration");
      return false;
    }

    try {
      console.log("Configuring image upload for user:", userId);
      
      // Set up the onUpload handler
      cesdk.configuration.callbacks = cesdk.configuration.callbacks || {};
      cesdk.configuration.callbacks.onUpload = async (file) => {
        console.log("Upload callback triggered with file:", file);
        
        try {
          // Validate file
          if (!file || !(file instanceof File)) {
            throw new Error('Invalid file object');
          }
          
          // Check file type
          const validTypes = ['image/jpeg', 'image/png', 'image/gif'];
          if (!validTypes.includes(file.type)) {
            throw new Error('Unsupported file type. Please upload a JPG, PNG, or GIF.');
          }
          
          // Check file size (max 12MB)
          const maxSize = 12 * 1024 * 1024; // 12MB
          if (file.size > maxSize) {
            throw new Error('File too large. Maximum size is 12MB.');
          }
          
          // Generate unique filename
          const fileExt = file.name.split('.').pop().toLowerCase();
          const fileName = `upload_${Date.now()}_${Math.random().toString(36).substring(2, 15)}.${fileExt}`;
          const filePath = `user_uploads/${userId}/${fileName}`;
          
          console.log(`Uploading file to ${filePath}...`);
          
          // Upload file to Supabase Storage
          const { data: uploadData, error: uploadError } = await supabase.storage
            .from('playmakerpics-images')
            .upload(filePath, file, {
              cacheControl: '3600',
              upsert: true,
              contentType: file.type
            });
            
          if (uploadError) {
            throw new Error(`Storage upload failed: ${uploadError.message}`);
          }
          
          // Get public URL
          const { data: { publicUrl } } = supabase.storage
            .from('playmakerpics-images')
            .getPublicUrl(filePath);
            
          console.log("File uploaded successfully:", publicUrl);
          
          // Return image object in the format expected by CESDK
          const imageAsset = {
            id: `upload-${Date.now()}`,
            label: file.name.split('.')[0],
            kind: 'image',
            meta: {
              uri: publicUrl + `?t=${Date.now()}`,
              thumbUri: publicUrl + `?t=${Date.now()}`,
              blockType: '//ly.img.ubq/graphic',
              fillType: '//ly.img.ubq/fill/image',
              shapeType: '//ly.img.ubq/shape/rect',
              width: 1200,
              height: 800,
              mimeType: file.type
            }
          };

          try {
            cesdk.engine.asset.addAssetToSource?.('user-uploads', imageAsset);
            cesdk.engine.asset.assetSourceContentsChanged?.('user-uploads');
          } catch (_) {/* ignore if source not ready */}

          return imageAsset;
        } catch (error) {
          console.error("Upload error:", error);
          return Promise.reject(error);
        }
      };
      
      return true;
    } catch (err) {
      console.error('Error configuring image upload:', err);
      return false;
    }
  }, []);

  // Add debug functions to window for testing
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.debugPlaceholders = () => {
        console.log("Testing placeholder detection...");
        const placeholders = getPlaceholders();
        console.log("Result:", placeholders);
        return placeholders;
      };
      
             // Helper function to mark selected blocks as placeholders
       window.markSelectedAsPlaceholder = () => {
         console.log("Marking selected blocks as placeholders...");
         const cesdk = cesdkRef.current;
         if (!cesdk) {
           console.error("CESDK not available");
           return;
         }
         
         try {
           const selectedBlocks = cesdk.engine.block.findAllSelected();
           
           if (selectedBlocks.length === 0) {
             console.log("No blocks selected. Please select a text or graphic block first.");
             return;
           }
           
           selectedBlocks.forEach(blockId => {
             try {
               const name = cesdk.engine.block.getName(blockId) || `Block ${blockId}`;
               const type = cesdk.engine.block.getType(blockId);
               
               const success = setBlockAsPlaceholder(blockId, true);
               if (success) {
                 console.log(`Successfully marked "${name}" (${type}) as placeholder`);
               } else {
                 console.log(`Failed to mark "${name}" as placeholder`);
               }
             } catch (e) {
               console.error(`Error marking block ${blockId}:`, e);
             }
           });
           
           // Test detection after marking
           setTimeout(() => {
             window.debugPlaceholders();
           }, 100);
           
         } catch (error) {
           console.error("Error in markSelectedAsPlaceholder:", error);
         }
       };
      
                    // Helper function to get all blocks in scene for debugging
       window.debugAllBlocks = () => {
         console.log("Listing all blocks in scene...");
         const cesdk = cesdkRef.current;
         if (!cesdk) {
           console.error("CESDK not available");
           return;
         }
         
         try {
           const scene = cesdk.engine.scene.get();
           const pages = cesdk.engine.block.getChildren(scene);
           
           console.log(`Found ${pages.length} pages`);
           
           const allBlocks = [];
           pages.forEach((page, pageIndex) => {
             const pageChildren = cesdk.engine.block.getChildren(page);
             console.log(`Page ${pageIndex + 1}: ${pageChildren.length} direct children`);
             
             pageChildren.forEach(blockId => {
               try {
                 const name = cesdk.engine.block.getName(blockId) || "(unnamed)";
                 const type = cesdk.engine.block.getType(blockId);
                 allBlocks.push({ id: blockId, name, type });
                 console.log(`  Block: "${name}" (${type}) ID: ${blockId}`);
               } catch (e) {
                 console.log(`  Error reading block ${blockId}:`, e.message);
               }
             });
           });
           
           return allBlocks;
         } catch (error) {
           console.error("Error in debugAllBlocks:", error);
         }
       };
    }
  }, [getPlaceholders, setBlockAsPlaceholder]);

  // Return hook interface
  return {
    cesdk: cesdkRef.current,
    status,
    error,
    initialize,
    createBlankScene,
    registerAssetSources,
    loadTemplates,
    configureUI,
    loadSceneFromSupabase,
    saveSceneToSupabase,
    getPlaceholders,
    setBlockAsPlaceholder,
    configureImageUpload,
    cleanup,
    isReady: status === 'ready'
  };
}